import { useRouter } from "../../store/router";
import { useI18n } from "../../i18n/useI18n";
import { useBranding } from "../../hooks/useBranding";
import { styleName, useMotionsForStyle, useOutputAspect, useSampleVideoUrl, useSelectedStyle } from "../../hooks/useStyles";
import { useVideoLoading } from "../../hooks/useVideoLoading";
import { BackButton } from "../../components/ui/BackButton";
import { Button } from "../../components/ui/Button";
import { Lines } from "../../components/ui/Lines";
import { PhotoPrint } from "../../components/ui/PhotoPrint";
import { afterIntroRoute } from "./videoFlow";
import { useBoothCaptionEnabled, useBoothPayment } from "../../store/booth";
import { anyPaymentActive, receiveRoute } from "../Payment/paymentFlow";

/**
 * STAGE · VideoIntro (HERIT 캔버스 9.1 "움직이는 화보", BOOTH-60 1b-2) — "영상으로도 만들기" 다음에 **항상** 나오는 소개 화면.
 * 사진 출력 비율(활성 프리셋, 기본 3:4) 인화지 프레임에 운영자가 올린 샘플 영상(테마 → 부스) 을 루프 재생하고, 샘플이 없으면 손님의 변환 사진을 대신 보여준다.
 * 프레임을 9:16 으로 두지 않는 이유 — Veo 9:16 결과물의 위아래 검은 띠를 3:4 프레임 + cover 로 잘라내고(PrintShareView 영상 카드와 동일), 사진 폴백은 크롭 없이 보인다.
 * "만들기" → 동작 선택(동작이 있으면) 또는 자막 입력(BOOTH-85) — 결제 유효 ON 이면 자막 화면이 생성 직전에 결제를 끼운다 (BOOTH-61/74).
 * 부스 자막 단계가 꺼져 있고(`captionEnabled: false`) 동작도 없으면 이 화면이 생성 직전이라 여기서 결제 분기를 맡는다.
 * "사진만 할게요" → 결제 유효 ON 이면 사진 1행 결제부터.
 */
export function VideoIntroView() {
  const go = useRouter((s) => s.go);
  const clearVideo = useRouter((s) => s.clearVideo);
  const clearMotions = useRouter((s) => s.clearMotions);
  const beginPayment = useRouter((s) => s.beginPayment);
  const clearVideoCaption = useRouter((s) => s.clearVideoCaption);
  const photo = useRouter((s) => s.capturedShots[0]?.transformedDataUrl ?? s.capturedShots[0]?.dataUrl ?? null);
  const { t, language } = useI18n();
  const { wordmark } = useBranding();
  const style = useSelectedStyle();
  const motions = useMotionsForStyle(style);
  const sampleUrl = useSampleVideoUrl();
  const aspect = useOutputAspect();
  const pay = useBoothPayment();
  const captionEnabled = useBoothCaptionEnabled();
  // 샘플 영상 첫 프레임 전 쉬머 (BOOTH-93) — 사진 폴백일 땐 쓰지 않는다
  const { loading: sampleLoading, bind: sampleBind } = useVideoLoading();

  // 소개에서 다시 시작하면 이전 선택(동작·자막)을 비운다 — 다음 화면들이 처음부터 받는다
  const start = () => {
    clearVideo();
    clearMotions();
    clearVideoCaption();
    const route = afterIntroRoute(motions.length, captionEnabled, anyPaymentActive(pay));
    // 동작 없음 + 자막 끔 = 이 화면이 생성 직전 — 결제가 걸리면 영상 포함 결제부터 (자막 화면이 하던 일)
    if (route === "payment") beginPayment(true);
    go(route);
  };

  // 사진만 — 사진 결제가 걸린 부스만 사진 1행 결제부터
  const photoOnly = () => {
    const route = receiveRoute(pay.photoActive);
    if (route === "payment") beginPayment(false);
    go(route);
  };

  return (
    <div className="view view--stackable view--intro">
      <BackButton label={t.videoIntro.back} onClick={() => go("review")} />
      <section className="split">
        <div className="split__media">
          <PhotoPrint
            aspect={aspect}
            brand={wordmark}
            label={styleName(style, language)}
            src={sampleUrl ? undefined : photo ?? undefined}
            overlay={sampleUrl && sampleLoading ? <span className="shimmer" aria-hidden /> : null}
          >
            {sampleUrl ? (
              <video key={sampleUrl} {...sampleBind} src={sampleUrl} autoPlay loop muted playsInline preload="auto" />
            ) : null}
          </PhotoPrint>
        </div>
        <div className="split__copy">
          <span className="pill pill--style">{styleName(style, language)}</span>
          <h1><Lines text={t.videoIntro.title} /></h1>
          <p className="intro__body"><Lines text={t.videoIntro.body} /></p>
          <div className="actions actions--pair">
            <Button variant="secondary" onClick={photoOnly}>{t.videoIntro.skip}</Button>
            <Button onClick={start}>{t.videoIntro.cta}</Button>
          </div>
        </div>
      </section>
    </div>
  );
}
